import styled, { keyframes } from "styled-components";
import { color } from "../../../shared/styles";

export const OutsourceDetailColumns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    min-width: 0;

    > * {
        min-width: 0;
    }

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const modalFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translate(-50%, -54%);
    }
    to {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
`;

const modalFadeOut = keyframes`
    from {
        opacity: 1;
        transform: translate(-50%, -50%);
    }
    to {
        opacity: 0;
        transform: translate(-50%, -54%);
    }
`;

export const SelectorPaneHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

export const SelectorPaneTitle = styled.div`
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    color: ${color.secondary.CHARCOAL};
`;

export const SelectorSearchInput = styled.input`
    width: 100%;
    height: 38px;
    box-sizing: border-box;
    border: 1px solid ${color.secondary.BORDER};
    border-radius: 6px;
    padding: 0 12px;
    font-family: Noto Sans;
    font-size: 14px;
    color: ${color.secondary.CHARCOAL};
    outline: none;

    &:focus {
        border-color: ${color.primary.BLUE_B100};
        box-shadow: 0 0 0 2px rgba(0, 88, 160, 0.12);
    }
`;

export const AddMaterialModalFrame = styled.div<{ $isOpen: boolean }>`
    width: min(980px, 92vw);
    max-height: 88vh;
    padding: 24px;
    border-radius: 8px;
    background-color: ${color.emphasis.NEUTRAL_N0};
    border: 1px solid ${color.secondary.BORDER};
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    gap: 18px;
    font-family: Noto Sans;
    input { font-family: inherit; font-size: 13px; }
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    animation: ${({ $isOpen }) => ($isOpen ? modalFadeIn : modalFadeOut)} 0.22s ease-out forwards;

    @media (max-width: 720px) {
        width: 94vw;
        min-width: 0;
        padding: 18px;
    }
`;

export const AddMaterialModalBody = styled.div`
    min-height: 0;
    max-height: calc(88vh - 150px);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const ModalHeaderRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;

    @media (max-width: 720px) {
        flex-direction: column;
    }
`;

export const ModalStepRail = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

export const ModalStepItem = styled.div<{ $active?: boolean; $done?: boolean }>`
    min-height: 34px;
    padding: 5px 10px 5px 6px;
    border-radius: 999px;
    border: 1px solid ${({ $active }) => ($active ? color.primary.BLUE_B100 : color.secondary.BORDER)};
    background-color: ${({ $active, $done }) =>
        $active ? color.status.INFO_LIGHT : $done ? color.emphasis.NEUTRAL_N5 : color.emphasis.NEUTRAL_N0};
    color: ${({ $active }) => ($active ? color.primary.BLUE_B100 : color.secondary.CHARCOAL)};
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
`;

export const ModalStepNumber = styled.span<{ $active?: boolean; $done?: boolean }>`
    width: 22px;
    height: 22px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ $active, $done }) =>
        $active || $done ? color.primary.BLUE_B100 : color.secondary.LIGHT_GREY};
    color: ${({ $active, $done }) =>
        $active || $done ? color.emphasis.NEUTRAL_N0 : color.secondary.CHARCOAL};
    font-size: 12px;
    font-weight: 800;
`;

export const MaterialPickerLayout = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 16px;
    min-height: 0;

    @media (max-width: 980px) {
        grid-template-columns: 1fr;
    }
`;

export const MaterialPickerPanel = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const MaterialPickerList = styled.div`
    max-height: 470px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-right: 4px;
`;

export const MaterialPickerRowCard = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
    min-height: 70px;
    padding: 12px;
    border-radius: 7px;
    border: 1px solid ${({ $selected }) => ($selected ? color.primary.BLUE_B100 : color.secondary.BORDER)};
    background-color: ${({ $selected, $disabled }) =>
        $selected ? color.status.INFO_LIGHT : $disabled ? color.emphasis.NEUTRAL_N5 : color.emphasis.NEUTRAL_N0};
    opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: center;
`;

export const MaterialUnavailableReason = styled.div`
    color: ${color.status.FAIL};
    font-size: 13px;
    line-height: 18px;
`;

export const MaterialIdentity = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

export const MaterialTitleLine = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
`;

export const MaterialName = styled.div`
    max-width: 100%;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    color: ${color.secondary.CHARCOAL};
    overflow-wrap: anywhere;
`;

export const MaterialMetaLine = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: ${color.emphasis.NEUTRAL_N70};
`;

export const SelectedSummaryPanel = styled.div`
    min-width: 0;
    padding: 14px;
    border-radius: 7px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N5};
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-self: start;
`;

export const SelectedChipList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 370px;
    overflow-y: auto;
`;

export const SelectedChip = styled.div`
    padding: 9px 10px;
    border-radius: 6px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N0};
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: ${color.secondary.CHARCOAL};

    span {
        color: ${color.emphasis.NEUTRAL_N70};
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;
    }
`;

export const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const ReviewMaterialCard = styled.div`
    padding: 14px;
    border-radius: 7px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.emphasis.NEUTRAL_N0};
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ReviewMaterialHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

export const ReviewRemoveAction = styled.div`
    margin-left: auto;
`;

export const ReviewField = styled.label`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    color: ${color.secondary.CHARCOAL};
`;

export const ReviewQuantityRow = styled.div`
    width: min(240px, 100%);

    input {
        width: 100%;
        box-sizing: border-box;
    }
`;

export const ReviewSourceTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: ${color.secondary.CHARCOAL};
`;

export const ReviewSourceTableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
    border: 1px solid ${color.secondary.BORDER};
    border-radius: 7px;
`;

export const ReviewSourceTable = styled.table`
    width: 100%;
    min-width: 620px;
    border-collapse: collapse;
    font-size: 13px;
    color: ${color.secondary.CHARCOAL};

    th,
    td {
        padding: 10px 12px;
        text-align: left;
        border-bottom: 1px solid ${color.secondary.BORDER};
    }

    th {
        background-color: ${color.emphasis.NEUTRAL_N5};
        font-weight: 700;
        white-space: nowrap;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4),
    th:nth-child(5),
    td:nth-child(5) {
        text-align: center;
    }
`;

export const ReviewSummaryBar = styled.div`
    padding: 12px 14px;
    border-radius: 7px;
    border: 1px solid ${color.secondary.BORDER};
    background-color: ${color.secondary.LIGHT_GREY};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    color: ${color.secondary.CHARCOAL};
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
`;

export const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
    margin-top: auto;

    @media (max-width: 600px) {
        flex-direction: column-reverse;
    }
`;
